// Apolo IDE — SidePanel: Live preview tab (Sprint v11 E11).
//
// Listens for `apolo:livePreview` events {filename, source, mode='auto'} to
// render a React or HTML preview inside a sandboxed iframe. JSX/TSX/TS goes
// through esbuild-wasm; HTML is dropped straight in. Save events re-render
// without losing the iframe scroll position.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Eye, Loader2, RefreshCw } from 'lucide-react';
import { buildHtmlSrcdoc, buildReactSrcdoc, transformJsx } from '../../../lib/livePreview.js';

const SAMPLE = [
  'export default function Demo() {',
  '  return (',
  '    <div>',
  '      <h1>Apolo Live Preview</h1>',
  '      <p>Dispatche apolo:livePreview com filename + source.</p>',
  '    </div>',
  '  );',
  '}',
  '',
].join('\n');

function isJsxFile(filename) {
  if (!filename) return false;
  return /\.(jsx|tsx|ts|js|mjs)$/i.test(filename);
}

function isHtmlFile(filename) {
  if (!filename) return false;
  return /\.(html|htm)$/i.test(filename);
}

export default function PreviewTab() {
  const [filename, setFilename] = useState('Demo.jsx');
  const [source, setSource] = useState(SAMPLE);
  const [srcdoc, setSrcdoc] = useState('');
  const [status, setStatus] = useState({ phase: 'idle', error: null });
  const iframeRef = useRef(null);

  const renderPreview = useCallback(async (filenameNext, sourceNext) => {
    setStatus({ phase: 'building', error: null });
    try {
      let doc;
      if (isHtmlFile(filenameNext)) {
        doc = buildHtmlSrcdoc(sourceNext);
      } else if (isJsxFile(filenameNext)) {
        const transformed = await transformJsx(sourceNext, filenameNext);
        doc = buildReactSrcdoc(transformed, filenameNext);
      } else {
        throw new Error(`Tipo não suportado: ${filenameNext}`);
      }
      setSrcdoc(doc);
      setStatus({ phase: 'ready', error: null });
    } catch (err) {
      setStatus({ phase: 'error', error: err?.message || 'Falha no transform' });
    }
  }, []);

  // Initial render on mount.
  useEffect(() => {
    renderPreview(filename, source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for external preview events.
  useEffect(() => {
    function onLivePreview(e) {
      const { filename: f, source: s } = e.detail || {};
      if (typeof f === 'string' && typeof s === 'string') {
        setFilename(f);
        setSource(s);
        renderPreview(f, s);
      }
    }
    function onFileSaved(e) {
      const { filePath, content } = e.detail || {};
      if (filePath === filename && typeof content === 'string') {
        setSource(content);
        renderPreview(filename, content);
      }
    }
    window.addEventListener('apolo:livePreview', onLivePreview);
    window.addEventListener('apolo:fileSaved', onFileSaved);
    return () => {
      window.removeEventListener('apolo:livePreview', onLivePreview);
      window.removeEventListener('apolo:fileSaved', onFileSaved);
    };
  }, [filename, renderPreview]);

  const reload = useCallback(() => {
    renderPreview(filename, source);
  }, [filename, source, renderPreview]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2 text-xs">
        <div className="flex min-w-0 items-center gap-1">
          <Eye size={11} className="text-apolo-textTertiary" aria-hidden="true" />
          <code className="truncate text-apolo-textPrimary">{filename}</code>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {status.phase === 'building' && (
            <span className="inline-flex items-center gap-1 text-apolo-textTertiary">
              <Loader2 size={11} className="animate-spin" aria-hidden="true" />
              Build…
            </span>
          )}
          {status.phase === 'ready' && (
            <span className="text-[10px] text-apolo-success">Pronto</span>
          )}
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle px-2 py-1 text-[11px] text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary"
          >
            <RefreshCw size={11} aria-hidden="true" />
            Recarregar
          </button>
        </div>
      </header>

      {status.error && (
        <div role="alert" className="border-b border-apolo-error/30 bg-apolo-errorBg px-3 py-2 text-xs text-apolo-error">
          <AlertCircle size={11} className="mr-1 inline" aria-hidden="true" />
          {status.error}
        </div>
      )}

      <div className="flex-1 overflow-hidden bg-white">
        <iframe
          ref={iframeRef}
          title="Apolo live preview"
          srcDoc={srcdoc}
          sandbox="allow-scripts"
          className="h-full w-full border-0"
        />
      </div>
    </div>
  );
}
